import React, { useState, useContext, useRef, useEffect, useCallback } from "react";
import { Container, Form, Button } from "react-bootstrap";
import MaterialTable from "material-table";
import { useHistory } from "react-router-dom";
import { BsSearch, BsXCircle, BsChevronDown, BsChevronUp, BsArrowRight, BsArrowClockwise } from "react-icons/bs";
import ApiService from "../ApiService";
import { parseAxiosError } from "../util/parserHelper";
import { SnackbarContext } from "./AlertSystem";
import "./PatientSearchPage.css";

const PatientSearchPage = () => {
  const [emailSearch, setEmailSearch] = useState("");
  const [dobSearch, setDobSearch] = useState("");
  const [phoneSearch, setPhoneSearch] = useState("");
  const [kitSearch, setKitSearch] = useState("");
  const [nameSearch, setNameSearch] = useState("");
  const [orderIdSearch, setOrderIdSearch] = useState("");
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const { showSnackbar } = useContext(SnackbarContext);
  const navigate = useHistory();
  const primaryInputRef = useRef(null);

  const handleSnackOpen = (text, severity = "info") => {
    showSnackbar?.({ message: text, severity, autoHide: 6000 });
  };

  const navigatePatientProfile = (id) => {
    navigate.push({
      pathname: "PatientProfile",
      search: `?patientId=${id}&idType=sub`,
    });
  };

  const hasAnyFilter = emailSearch.trim() || dobSearch.trim() || phoneSearch.trim() || kitSearch.trim() || nameSearch.trim() || orderIdSearch.trim();

  const handleMultiFilterSearch = useCallback(async () => {
    const promises = [];
    if (emailSearch.trim()) {
      promises.push(ApiService.searchPatient({ type: "patient_email", term: emailSearch.toLowerCase() }));
    }
    if (dobSearch.trim()) {
      promises.push(ApiService.searchPatient({ type: "patient_dob", term: dobSearch }));
    }
    if (phoneSearch.trim()) {
      const normalizedPhone = phoneSearch.replace(/\D/g, "").slice(-10);
      promises.push(ApiService.searchPatient({ type: "phone", term: normalizedPhone }));
    }
    if (kitSearch.trim()) {
      promises.push(ApiService.searchPatient({ type: "patient_kit_id", term: kitSearch }));
    }
    if (orderIdSearch.trim()) {
      promises.push(ApiService.searchPatient({ type: "order_id", term: orderIdSearch }));
    }
    if (nameSearch.trim()) {
      promises.push(ApiService.searchPatient({ type: "patient_name", term: nameSearch }));
    }
    if (promises.length === 0) {
      handleSnackOpen("Please enter at least one search criteria.", "error");
      return;
    }
    try {
      setLoading(true);
      setHasSearched(true);
      const responses = await Promise.all(promises);
      let combinedResults = [];
      responses.forEach((response) => {
        if (response?.data) combinedResults = combinedResults.concat(response.data);
      });
      const uniqueResults = [...new Map(combinedResults.map((item) => [item.id, item])).values()];
      setTableData(uniqueResults);
      if (uniqueResults.length === 0) {
        handleSnackOpen("No patients found for the provided filters.", "warning");
      }
    } catch (error) {
      handleSnackOpen(`Patient search failed. ${parseAxiosError(error)?.message}`, "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailSearch, dobSearch, phoneSearch, kitSearch, orderIdSearch, nameSearch]);

  const handleClearFilters = () => {
    setEmailSearch("");
    setDobSearch("");
    setPhoneSearch("");
    setKitSearch("");
    setNameSearch("");
    setOrderIdSearch("");
    setTableData([]);
    setHasSearched(false);
    primaryInputRef.current?.focus();
  };

  const handleFormKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (loading || !hasAnyFilter) return;
      handleMultiFilterSearch();
    }
  };

  const isMac = typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const isEditing = tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable;
      const isTwoKeySearch = (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");

      if (isTwoKeySearch && !isEditing) {
        e.preventDefault();
        setShowAdvanced(true);
        primaryInputRef.current?.focus();
        return;
      }
      if (e.key === "Escape") {
        if (document.activeElement?.closest(".psp-search-section")) {
          primaryInputRef.current?.blur();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const resultCount = tableData.length;

  return (
    <Container fluid className="psp-container">
      {/* Page header */}
      <div className="psp-header">
        <div>
          <h1 className="psp-title">Patient Search</h1>
          <p className="psp-subtitle">
            Find patients by name, email, phone, or advanced filters.
            <span className="psp-shortcut" title={isMac ? "Focus search and show filters: ⌘K" : "Focus search and show filters: Ctrl+K"}>
              {isMac ? (
                <>
                  <kbd>⌘</kbd><kbd>K</kbd>
                </>
              ) : (
                <>
                  <kbd>Ctrl</kbd><kbd>K</kbd>
                </>
              )}
            </span>
          </p>
        </div>
      </div>

      {/* Search section */}
      <div className="psp-search-section">
        <Form onKeyDown={handleFormKeyDown} className="psp-search-form">
          {/* Primary search row */}
          <div className="psp-primary-row">
            <div className="psp-field psp-field--name">
              <Form.Label className="psp-label">Name</Form.Label>
              <Form.Control
                ref={primaryInputRef}
                type="text"
                placeholder="Patient name"
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                className="psp-input"
                autoComplete="off"
              />
            </div>
            <div className="psp-field psp-field--email">
              <Form.Label className="psp-label">Email</Form.Label>
              <Form.Control
                type="text"
                placeholder="patient@example.com"
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                className="psp-input"
                autoComplete="off"
              />
            </div>
            <div className="psp-field psp-field--phone">
              <Form.Label className="psp-label">Phone</Form.Label>
              <Form.Control
                type="text"
                placeholder="(404) 789-7890"
                value={phoneSearch}
                onChange={(e) => setPhoneSearch(e.target.value)}
                className="psp-input"
                autoComplete="off"
              />
            </div>
            <Button
              type="button"
              className="psp-search-btn"
              onClick={handleMultiFilterSearch}
              disabled={loading || !hasAnyFilter}
              aria-label="Search patients"
            >
              {loading ? <BsArrowClockwise size={16} className="psp-spin" /> : <BsSearch size={16} />}
              {loading ? "Searching…" : "Search"}
            </Button>
          </div>

          {/* Advanced toggle + clear */}
          <div className="psp-controls-row">
            <button
              type="button"
              className="psp-toggle-advanced"
              onClick={() => setShowAdvanced((prev) => !prev)}
              aria-expanded={showAdvanced}
            >
              {showAdvanced ? <BsChevronUp size={14} /> : <BsChevronDown size={14} />}
              {showAdvanced ? "Hide advanced filters" : "More filters"}
              {!showAdvanced && (kitSearch || dobSearch || orderIdSearch) && (
                <span className="psp-badge">{[kitSearch, dobSearch, orderIdSearch].filter((v) => v.trim()).length}</span>
              )}
            </button>
            {hasAnyFilter && (
              <button type="button" className="psp-clear-btn" onClick={handleClearFilters}>
                <BsXCircle size={14} />
                Clear all
              </button>
            )}
            {hasSearched && resultCount > 0 && (
              <span className="psp-result-count">
                {resultCount} patient{resultCount !== 1 ? "s" : ""} found
              </span>
            )}
          </div>

          {/* Advanced filters (progressive disclosure) */}
          {showAdvanced && (
            <div className="psp-advanced-row">
              <div className="psp-field">
                <Form.Label className="psp-label">Date of Birth</Form.Label>
                <Form.Control
                  type="date"
                  value={dobSearch}
                  onChange={(e) => setDobSearch(e.target.value)}
                  className="psp-input"
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
              <div className="psp-field">
                <Form.Label className="psp-label">Kit ID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="TBD0001234"
                  value={kitSearch}
                  onChange={(e) => setKitSearch(e.target.value)}
                  className="psp-input"
                  autoComplete="off"
                />
              </div>
              <div className="psp-field">
                <Form.Label className="psp-label">Master / Order ID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="260216921729A272"
                  value={orderIdSearch}
                  onChange={(e) => setOrderIdSearch(e.target.value)}
                  className="psp-input"
                  autoComplete="off"
                />
              </div>
            </div>
          )}
        </Form>
      </div>

      {/* Results */}
      <div className="psp-results-section">
        {!hasSearched ? (
          <div className="psp-empty-state">
            <BsSearch size={40} className="psp-empty-icon" />
            <p className="psp-empty-title">Search for a patient</p>
            <p className="psp-empty-text">Enter a name, email, or phone number above and press <strong>Enter</strong> or click <strong>Search</strong>.</p>
          </div>
        ) : (
          <MaterialTable
            title=""
            columns={[
              {
                title: "Patient",
                field: "firstName",
                render: (rowData) => (
                  <div className="psp-cell-patient">
                    <span className="psp-cell-name">{rowData.firstName} {rowData.lastName}</span>
                    <span className="psp-cell-email">{rowData.email}</span>
                  </div>
                ),
                cellStyle: { minWidth: 240, verticalAlign: "top", padding: "12px 16px" },
                headerStyle: { minWidth: 240 },
              },
              {
                title: "DOB",
                field: "dob",
                cellStyle: { minWidth: 120, padding: "12px 16px" },
                headerStyle: { minWidth: 120 },
              },
              {
                title: "Phone",
                field: "phoneNumber",
                cellStyle: { minWidth: 140, padding: "12px 16px" },
                headerStyle: { minWidth: 140 },
              },
              {
                title: "Location",
                field: "state",
                cellStyle: { minWidth: 100, padding: "12px 16px" },
                headerStyle: { minWidth: 100 },
              },
              {
                title: "",
                field: "action",
                filtering: false,
                sorting: false,
                render: (rowData) => (
                  <button
                    type="button"
                    className="psp-view-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigatePatientProfile(rowData.id);
                    }}
                    aria-label={`View details for ${rowData.firstName} ${rowData.lastName}`}
                  >
                    View <BsArrowRight size={14} />
                  </button>
                ),
                cellStyle: { width: 110, textAlign: "right", padding: "12px 16px" },
                headerStyle: { width: 110, textAlign: "right" },
              },
            ]}
            data={tableData}
            onRowClick={(_, rowData) => navigatePatientProfile(rowData.id)}
            options={{
              filtering: true,
              exportButton: true,
              headerStyle: {
                position: "sticky",
                top: 0,
                backgroundColor: "#f9fafb",
                color: "#6b7280",
                fontWeight: "600",
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                borderBottom: "2px solid #e5e7eb",
                padding: "10px 16px",
              },
              maxBodyHeight: "calc(100vh - 340px)",
              search: false,
              pageSize: 50,
              pageSizeOptions: [25, 50, 100, 200],
              emptyRowsWhenPaging: false,
              draggable: false,
              toolbarButtonAlignment: "right",
              exportAllData: true,
              sorting: true,
              rowStyle: (_, index) => ({
                backgroundColor: index % 2 === 0 ? "#fff" : "#f9fafb",
                fontSize: "0.9rem",
                color: "#1f2937",
                cursor: "pointer",
              }),
            }}
            localization={{
              body: {
                emptyDataSourceMessage: (
                  <div className="psp-table-empty">
                    No patients match your search criteria.
                  </div>
                ),
              },
              toolbar: { exportTitle: "Export", exportAriaLabel: "Export patients" },
            }}
          />
        )}
      </div>
    </Container>
  );
};

export default PatientSearchPage;
